import {
  ApartmentOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  MessageOutlined,
  TagsOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { useCallback } from 'react';

import { ConfirmDeletingElement } from '@app/components';
import { MassActionTypeModal } from '@app/components/Modals';
import { useSendout } from '@app/features/SendoutCreationModal';
import { TagsPickerModal } from '@app/shared/components';

import {
  useLeadsRegistryFilterData,
  useLeadsRegistryMassActions,
  useLeadsRegistrySelection,
} from '../../../../hooks';
import { LeadsRegistryAssignModal } from '../../LeadsRegistryAssignModal';
import { LeadsRegistryFunnelStageModal } from '../../LeadsRegistryFunnelStageModal';

import type { MenuProps } from 'antd';
import type { FC } from 'react';

import './LeadsRegistryMassActions.scss';

// Registry mass-action toolbar host: selected count, actions menu, and
// clear-selection control.
export const LeadsRegistryMassActions: FC = () => {
  const { selectedLeadIds, clearSelection } = useLeadsRegistrySelection();
  const { assigneeOptions, funnels } = useLeadsRegistryFilterData();
  const { openSendMessageModal, renderSendMessageModal } = useSendout();
  const {
    activeBranchId,
    searchId,
    getCurrentSearchId,
    allCount,
    hasLeads,
    selectedClientIds,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isDeleting,
    confirmDelete,
    isAssignModalOpen,
    setIsAssignModalOpen,
    assignUserId,
    setAssignUserId,
    isAssigning,
    confirmAssign,
    isMarkingAsAnswered,
    confirmMarkNotRequiringAnswer,
    pendingMassAction,
    massActionType,
    isMassActionTypeModalOpen,
    openMassActionTypeModal,
    closeMassActionTypeModal,
    confirmMassActionType,
    isTagsModalOpen,
    closeTagsModal,
    selectedTagIds,
    setSelectedTagIds,
    availableTags,
    isTagsLoading,
    isTagsSaving,
    confirmSetTags,
    isRemoveTagsModalOpen,
    closeRemoveTagsModal,
    removeTagIds,
    setRemoveTagIds,
    isRemovingTags,
    confirmRemoveTags,
    isFunnelStageModalOpen,
    setIsFunnelStageModalOpen,
    isChangingFunnelStage,
    confirmChangeFunnelStage,
  } = useLeadsRegistryMassActions();

  const confirmMassActionTypeHandler = useCallback(
    (type: 'selected' | 'all') => {
      const isConfirmed = confirmMassActionType(type);

      if (!isConfirmed) return;

      if (pendingMassAction === 'mark-not-requiring-answer') {
        void confirmMarkNotRequiringAnswer(type);
        return;
      }

      if (pendingMassAction !== 'sendout') return;

      openSendMessageModal(
        {
          usage: 'leads',
          actionType: type,
          activeBranchId,
          selectedIds: selectedClientIds,
          searchId: searchId ?? undefined,
          getSearchId: getCurrentSearchId,
        },
        clearSelection
      );
    },
    [
      activeBranchId,
      clearSelection,
      confirmMarkNotRequiringAnswer,
      confirmMassActionType,
      getCurrentSearchId,
      openSendMessageModal,
      pendingMassAction,
      searchId,
      selectedClientIds,
    ]
  );

  if (!hasLeads) return null;

  const actionItems: MenuProps['items'] = [
    {
      key: 'assign',
      label: 'Назначить ответственного',
      icon: <UserOutlined />,
      onClick: () => openMassActionTypeModal('assign'),
    },
    {
      key: 'mark-not-requiring-answer',
      label: 'Не требует ответа',
      icon: <CheckOutlined />,
      disabled: isMarkingAsAnswered,
      onClick: () => openMassActionTypeModal('mark-not-requiring-answer'),
    },
    {
      key: 'funnel-stage',
      label: 'Изменить этап воронки',
      icon: <ApartmentOutlined />,
      onClick: () => openMassActionTypeModal('funnel-stage'),
    },
    {
      key: 'sendout',
      label: 'Отправить сообщение',
      icon: <MessageOutlined />,
      onClick: () => openMassActionTypeModal('sendout'),
    },
    {
      key: 'add-tags',
      label: 'Добавить теги',
      icon: <TagsOutlined />,
      onClick: () => openMassActionTypeModal('add-tags'),
    },
    {
      key: 'remove-tags',
      label: 'Убрать теги',
      icon: <TagsOutlined />,
      onClick: () => openMassActionTypeModal('remove-tags'),
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: 'Удалить',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => openMassActionTypeModal('delete'),
    },
  ];

  return (
    <div className="LeadsRegistryMassActions">
      <Dropdown menu={{ items: actionItems }} trigger={['click']}>
        <Button
          className="LeadsRegistryMassActions__actions"
          type="primary"
          size="small"
          data-testid="actions-button"
        >
          Действия
          <DownOutlined className="LeadsRegistryMassActions__actions-caret" />
        </Button>
      </Dropdown>

      {selectedLeadIds.length > 0 && (
        <span className="LeadsRegistryMassActions__count">
          Выбрано
          <span className="LeadsRegistryMassActions__count-badge">
            {selectedLeadIds.length}
          </span>
        </span>
      )}

      {selectedLeadIds.length > 0 && (
        <Tooltip title="Снять выделение">
          <Button
            className="LeadsRegistryMassActions__clear"
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="Снять выделение"
            onClick={clearSelection}
          />
        </Tooltip>
      )}

      <ConfirmDeletingElement
        modalOpen={isDeleteModalOpen}
        setModalOpen={setIsDeleteModalOpen}
        handleDelete={confirmDelete}
        isStageDeletionLoading={isDeleting}
        title="Удаление лидов"
        content={
          massActionType === 'all'
            ? `Вы уверены, что хотите удалить все лиды текущего фильтра (${allCount})?`
            : `Вы уверены, что хотите удалить выбранные лиды (${selectedLeadIds.length})?`
        }
      />

      <LeadsRegistryAssignModal
        open={isAssignModalOpen}
        options={assigneeOptions}
        selectedUserId={assignUserId}
        isLoading={isAssigning}
        onChange={setAssignUserId}
        onCancel={() => setIsAssignModalOpen(false)}
        onConfirm={confirmAssign}
      />

      <MassActionTypeModal
        type="leads"
        isOpen={isMassActionTypeModalOpen}
        onClose={closeMassActionTypeModal}
        onConfirm={confirmMassActionTypeHandler}
        disabledSelectedItemsActions={selectedLeadIds.length === 0}
        selectedCount={selectedLeadIds.length}
        allCount={allCount}
      />

      <TagsPickerModal
        open={isTagsModalOpen}
        title="Добавить теги"
        okText="Сохранить"
        availableTags={availableTags}
        selectedTagIds={selectedTagIds}
        isTagsLoading={isTagsLoading}
        isSaving={isTagsSaving}
        isConfirmDisabled={selectedTagIds.length === 0}
        onSelectedTagIdsChange={setSelectedTagIds}
        onCancel={closeTagsModal}
        onConfirm={confirmSetTags}
      />

      <TagsPickerModal
        open={isRemoveTagsModalOpen}
        title="Убрать теги"
        okText="Убрать"
        availableTags={availableTags}
        selectedTagIds={removeTagIds}
        isTagsLoading={isTagsLoading}
        isSaving={isRemovingTags}
        isConfirmDisabled={removeTagIds.length === 0}
        onSelectedTagIdsChange={setRemoveTagIds}
        onCancel={closeRemoveTagsModal}
        onConfirm={confirmRemoveTags}
      />

      <LeadsRegistryFunnelStageModal
        open={isFunnelStageModalOpen}
        funnels={funnels}
        isLoading={isChangingFunnelStage}
        onCancel={() => setIsFunnelStageModalOpen(false)}
        onConfirm={confirmChangeFunnelStage}
      />

      {renderSendMessageModal()}
    </div>
  );
};
